import React, { ReactNode, useLayoutEffect, useRef, useState } from "react"

import { BackgroundColor } from "../layout"
import Container from "../layout/container"
import { ContainerType } from "../layout/container"
import SliderNavigation from "./navigation"
import classNames from "classnames"
import { throttle } from "../../../shared/utils/throttle"
import { useAdminContext } from "react-bricks/frontend"
import { useMediaQuery } from "../../../hooks/use-media-query-admin"
import { useSlider } from "../../../shared/hooks/use-slider"

interface FixedWidthSliderProps {
    children: ReactNode
    length: number
    bgColor?: BackgroundColor
    hideBullets?: boolean
    footnotes?: ReactNode
    containerType?: ContainerType
}

const IS_SERVER = typeof window === "undefined"

const FixedWidthSlider = ({
    children,
    length,
    bgColor,
    hideBullets,
    footnotes,
    containerType,
}: FixedWidthSliderProps) => {
    const isMobile = useMediaQuery("(max-width: 767px)", { initializeWithValue: false })
    const isTabletPortrait = useMediaQuery("(max-width: 1023px)", { initializeWithValue: false })
    const isTabletLandscape = useMediaQuery("(max-width: 1365px)", { initializeWithValue: false })
    const isIPadPro = useMediaQuery("(max-width: 1559px)", { initializeWithValue: false })

    const sliderWrapperRef = useRef<null | HTMLDivElement>(null)
    const containerRef = useRef<HTMLDivElement | null>(null)
    const sliderRef = useRef<null | HTMLUListElement>(null)
    const [containerWidth, setContainerWidth] = useState(0)
    const [sliderWidth, setSliderWidth] = useState(0)

    const { isAdmin } = useAdminContext()

    const adminFrameDocument = !IS_SERVER
        ? (document.getElementById("rb-admin-frame") as HTMLIFrameElement | null)?.contentWindow?.document
        : undefined
    const adminContentFrameWindow = (adminFrameDocument?.getElementById("rb-content-frame") as HTMLIFrameElement | null)
        ?.contentWindow?.window

    useLayoutEffect(() => {
        if (!containerRef.current || !sliderRef.current) return

        const targetElement = isAdmin && adminContentFrameWindow ? adminContentFrameWindow : window

        const container = containerRef.current
        const slider = sliderRef.current

        const handleResize = () => {
            const computedStyle = getComputedStyle(container)
            const paddingLeft = parseFloat(computedStyle.paddingLeft)
            const paddingRight = parseFloat(computedStyle.paddingRight)
            setContainerWidth(container.clientWidth - paddingLeft - paddingRight)
            setSliderWidth(slider.offsetWidth)
        }
        handleResize()
        targetElement.addEventListener("resize", throttle(handleResize))
        return () => {
            targetElement.removeEventListener("resize", handleResize)
        }
    }, [isMobile, isTabletLandscape, isIPadPro, isAdmin])

    const childWidth =
        containerType === ContainerType.Redesign
            ? isMobile
                ? 280
                : isTabletPortrait
                ? 330
                : 393
            : isMobile
            ? 270
            : isTabletLandscape
            ? 323
            : isIPadPro
            ? 336
            : 360
    const gap =
        containerType === ContainerType.Redesign ? (isMobile ? 8 : isTabletLandscape ? 14 : 20) : isMobile ? 14 : 20
    const maxTranslateX =
        length > 0 ? (-1 * (length - 1) * (sliderWidth + gap)) / length + (containerWidth - childWidth) : 0
    const realLength =
        length > 0
            ? (-1 * maxTranslateX) / (childWidth + gap) + 1 < 0
                ? length
                : (-1 * maxTranslateX) / (childWidth + gap) + 1
            : 0

    const { handleDragStart, handleDragMove, handleDragEnd, index, lastIndex, transition, goTo } = useSlider(
        realLength,
        sliderWrapperRef,
        0
    )

    const translateX = length > 0 ? (-1 * index * (sliderWidth + gap)) / length : 0

    return (
        <div className="overflow-hidden" ref={sliderWrapperRef}>
            <Container ref={containerRef} type={containerType}>
                <ul
                    className={classNames("grid", {
                        "transition-transform duration-[250ms] ease-in-out": transition,
                        "cursor-grab active:cursor-grabbing": length > 1,
                    })}
                    style={{
                        width: length > 0 ? `calc(${childWidth}px * ${length} + ${gap}px * ${length - 1})` : "0px",
                        transform: `translateX(${translateX}px)`,
                        gridTemplateColumns: `repeat(${Math.max(length, 1)}, 1fr)`,
                        columnGap: `${gap}px`,
                    }}
                    onTouchStart={handleDragStart}
                    onMouseDown={handleDragStart}
                    onTouchMove={handleDragMove}
                    onMouseMove={handleDragMove}
                    onTouchEnd={handleDragEnd}
                    onMouseUp={handleDragEnd}
                    onMouseLeave={handleDragEnd}
                    role="none"
                    ref={sliderRef}
                >
                    {children}
                </ul>
                <SliderNavigation
                    length={Math.ceil(realLength)}
                    lastIndex={lastIndex}
                    goTo={goTo}
                    bgColor={bgColor}
                    hideBullets={hideBullets}
                    footnotes={footnotes}
                />
            </Container>
        </div>
    )
}

export default FixedWidthSlider
